'use client';

import { useRef, useEffect, useState } from 'react';
import { renderShareCard, type ShareCardData } from '@/lib/share-card-renderer';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { useCalendarStore } from '@/stores/calendar-store';
import { uiFb } from '@/lib/i18n';

interface ShareCardProps {
  data: ShareCardData;
  open: boolean;
  onClose: () => void;
}

const CARD_SIZE = 1080;

export function ShareCard({ data, open, onClose }: ShareCardProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const [sharing, setSharing] = useState(false);
  const lang = useCalendarStore((s) => s.lang);
  useFocusTrap(dialogRef, open);

  useEffect(() => {
    if (!open || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    canvas.width = CARD_SIZE;
    canvas.height = CARD_SIZE;
    renderShareCard(ctx, data, CARD_SIZE, CARD_SIZE);
  }, [open, data]);

  if (!open) return null;

  const handleDownload = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${data.title.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}-vacant-day.png`;
      a.click();
      URL.revokeObjectURL(url);
      window._paq?.push(['trackEvent', 'Share', 'ImageDownload', data.title]);
    }, 'image/png');
  };

  const handleShare = async () => {
    const canvas = canvasRef.current;
    if (!canvas || !navigator.canShare) return;
    setSharing(true);
    try {
      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
      if (!blob) return;
      const file = new File([blob], 'share-card.png', { type: 'image/png' });
      if (navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: data.title,
          files: [file],
        });
        window._paq?.push(['trackEvent', 'Share', 'ImageShare', data.title]);
      }
    } catch {
      // User cancelled or share failed
    } finally {
      setSharing(false);
    }
  };

  const canShare = typeof navigator !== 'undefined' && 'canShare' in navigator;

  return (
    <div
      ref={dialogRef}
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Share as image"
      onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-[var(--overlay-scrim)] backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="relative w-full max-w-md overflow-hidden rounded-2xl border border-border-primary bg-surface-sidebar shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border-subtle px-4 pt-4 pb-3">
          <h2 className="font-heading text-base font-bold text-text-primary">{uiFb('ui.share.title', 'Share as Image', lang)}</h2>
          <button
            onClick={onClose}
            className="rounded-lg p-1 text-muted transition-colors hover:bg-surface-hover hover:text-text-secondary"
            aria-label="Close"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Canvas preview */}
        <div className="p-4">
          <canvas
            ref={canvasRef}
            className="w-full rounded-lg"
            style={{ aspectRatio: '1/1' }}
          />
        </div>

        {/* Actions */}
        <div className="flex gap-2 border-t border-border-subtle p-4">
          <button
            onClick={handleDownload}
            className="flex flex-1 items-center justify-center gap-2 content-card py-2.5 text-sm font-medium text-text-secondary transition-colors hover:bg-surface-hover"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            {uiFb('ui.share.download', 'Download', lang)}
          </button>
          {canShare && (
            <button
              onClick={handleShare}
              disabled={sharing}
              className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-brand py-2.5 text-sm font-medium text-text-on-brand transition-colors hover:opacity-90 disabled:opacity-50"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
              </svg>
              {sharing ? uiFb('ui.share.sharing', 'Sharing\u2026', lang) : uiFb('ui.share.share', 'Share', lang)}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
